import { useEffect, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { ApiError, api, slotLabel, type LogisticsConfig, type LogisticsWeek, type Person, type PhysicianSummary, type Site, type TripPlan } from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';
import { navigate } from '../lib/router';
import { kindLabel } from './Residences';

const dr = (p: Person) => `Dr. ${p.lastName}`;
/** "Feb 2" — the weekday is implied by the page. */
const short = (d: string) => dayLabel(d).replace(/^\w+, /, '');

/**
 * The coordinator's week (D33): one hospital, one Tuesday — the same
 * sections as OSMD's "Tour Logistics & Handoffs" email, but read off the
 * published schedule and what physicians answered instead of retyped.
 * Rotation, handoffs and nights are facts; transportation and groceries
 * are the physicians' answers; housing is what the coordinator assigns.
 */
export function Logistics({ siteId, week, sites, physicians }: { siteId: string; week: string; sites: Site[]; physicians: PhysicianSummary[] }) {
  const [data, setData] = useState<LogisticsWeek | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assign, setAssign] = useState<{ physicianId: string; arriveOn: string; departOn: string } | null>(null);

  const [tracking, setTracking] = useState<LogisticsConfig | null>(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    api.logisticsConfig().then(setTracking).catch(() => setTracking(null));
  }, []);
  const load = () => {
    setError(null);
    api.logisticsWeek(siteId, week).then(setData).catch((e: Error) => setError(e.message));
  };
  const checkFlights = async () => {
    setChecking(true);
    try {
      await api.checkFlights();
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => {
    let ignore = false;
    setData(null);
    setError(null);
    api
      .logisticsWeek(siteId, week)
      .then((d) => { if (!ignore) setData(d); })
      .catch((e: Error) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [siteId, week]);
  useReloadOnChange(load);

  const go = (s: string, w: string) => navigate(`/admin/logistics?siteId=${s}&week=${w}`);
  const act = async (what: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await what();
      if (done) setToast(done);
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const propose = () =>
    act(async () => {
      const r = await api.proposeStays(siteId, week);
      const lines = [...r.proposed.map((p) => `${dr(p.physician)} → ${p.residence} · ${p.room}`), ...r.stuck.map((s) => `${dr(s.physician)}: ${s.why}`)];
      setToast(lines.length ? `Proposed: ${lines.join('; ')}` : 'Everyone already has a room.');
    });

  const siteName = sites.find((s) => s.id === siteId)?.name ?? siteId;
  return (
    <section className="logistics">
      <div className="gridtools noprint">
        <label className="field inline">
          <span className="muted">Hospital</span>
          <select value={siteId} onChange={(e) => go(e.target.value, week)}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="quarterpicker">
          <button type="button" onClick={() => go(siteId, addDays(week, -7))} aria-label="Previous week">‹</button>
          <h1>Tue {short(week)} – Tue {short(addDays(week, 7))}</h1>
          <button type="button" onClick={() => go(siteId, addDays(week, 7))} aria-label="Next week">›</button>
        </div>
        <button type="button" className="linklike" onClick={() => navigate(`/admin/logistics/residences?siteId=${siteId}`)}>Residences &amp; site guide</button>
        {/* A link here rather than only a tab (D101): the logistics TABS are rendered only for a
            login that is logistics and not admin, so an owner or scheduler never sees them and
            had no way to reach this screen but to type the address. */}
        <button type="button" className="linklike" onClick={() => navigate(`/admin/logistics/housing?week=${week}`)}>Housing capacity</button>
        <button type="button" className="linklike" onClick={() => navigate('/admin/logistics/transfer-center')}>Transfer Center sign-up</button>
        <button type="button" className="linklike" onClick={() => window.print()}>Print / save as PDF</button>
      </div>
      <h1 className="printonly">{siteName} — tour logistics, {dayLabel(week)} to {dayLabel(addDays(week, 7))}</h1>
      {error && <p className="error">Couldn't load the week ({error}).</p>}
      {problem && <p className="error">{problem}</p>}
      {toast && <p className="toast noprint" onClick={() => setToast(null)}>{toast}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data?.nearest && (
        <p className="notice">
          Nobody is scheduled at {data.site.name} this week.
          {data.nearest.next && <> <button type="button" className="linklike" onClick={() => go(siteId, data.nearest!.next!)}>Jump to the week of {short(data.nearest.next)} →</button></>}
          {data.nearest.previous && <> <button type="button" className="linklike" onClick={() => go(siteId, data.nearest!.previous!)}>← Back to the week of {short(data.nearest.previous)}</button></>}
        </p>
      )}
      {data && (
        <div className="logi-cols">
          <div className="logi-main">
            <Rotation data={data} />
            <Handoffs data={data} />
            <Nights data={data} />
            <Transportation data={data} tracking={tracking} checking={checking} onCheck={checkFlights} />
            <Groceries data={data} />
          </div>
          <div className="logi-side">
            <Housing data={data} busy={busy} onPropose={propose} onConfirm={(id) => act(() => api.confirmStay(id))} onRemove={(id) => act(() => api.removeStay(id))} onAssign={(p, a, d) => setAssign({ physicianId: p, arriveOn: a, departOn: d })} />
            <Misc data={data} />
          </div>
        </div>
      )}
      {assign && data && (
        <AssignDialog
          initial={assign}
          physicians={physicians}
          data={data}
          onClose={() => setAssign(null)}
          onSaved={() => { setAssign(null); setToast('Room assigned.'); load(); }}
        />
      )}
      <p className="legend muted noprint">
        Everything above comes from the published schedule and the physicians' own "before you go" answers; rooms are what you assign here. Physicians see only their own lines — and the address and door codes only from three days before they arrive.
      </p>
    </section>
  );
}

function Rotation({ data }: { data: LogisticsWeek }) {
  const { ends, starts, staysOn } = data.rotation;
  return (
    <div className="card logi">
      <h2>Rotation updates — Tuesday {short(data.week)}</h2>
      <h3>Ends shift (morning)</h3>
      {ends.length === 0 ? <p className="muted small">Nobody finishes a stint this Tuesday.</p> : (
        <ul className="people">{ends.map((p) => <li key={p.id}><strong>{dr(p)}</strong> <span className="muted">{p.positionSlots.map(slotLabel).join(', ')}{p.hours && ` · ${p.hours}`}</span></li>)}</ul>
      )}
      <h3>Starts shift (afternoon)</h3>
      {starts.length === 0 ? <p className="muted small">Nobody starts a stint this Tuesday.</p> : (
        <ul className="people">{starts.map((p) => <li key={p.id}><strong>{dr(p)}</strong> <span className="muted">{p.positionSlots.map(slotLabel).join(', ')}{p.hours && ` · ${p.hours}`} · departing Tue {short(p.through)}</span></li>)}</ul>
      )}
      <h3>Stays on</h3>
      {staysOn.length === 0 ? <p className="muted small">Nobody is mid-stint.</p> : (
        <ul className="people">{staysOn.map((p) => <li key={p.id}><strong>{dr(p)}</strong> <span className="muted">{p.hours && `${p.hours} · `}departing Tue {short(p.through)}</span></li>)}</ul>
      )}
    </div>
  );
}

function Handoffs({ data }: { data: LogisticsWeek }) {
  if (data.handoffs.length === 0) return null;
  return (
    <div className="card logi">
      <h2>Handoffs — Tuesday {short(data.week)} at {data.handoffs[0].at}</h2>
      <table className="plain">
        <thead><tr><th>Position</th><th>Out</th><th>In</th></tr></thead>
        <tbody>
          {data.handoffs.map((h) => (
            <tr key={h.positionSlot}>
              <td>{slotLabel(h.positionSlot)}</td>
              <td>{h.from ? dr(h.from) : <span className="muted">—</span>}</td>
              <td>{h.to ? dr(h.to) : <em className="err">nobody scheduled</em>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Nights({ data }: { data: LogisticsWeek }) {
  if (data.nights.length === 0) return null;
  return (
    <div className="card logi">
      <h2>Nights</h2>
      <table className="plain">
        <thead><tr><th>Night of</th><th>Position</th><th>Physician</th><th>Hours</th></tr></thead>
        <tbody>
          {data.nights.map((n, i) => (
            <tr key={i}>
              <td>{dayLabel(n.date)}</td>
              <td>{slotLabel(n.positionSlot)}</td>
              <td>{dr(n.physician)}</td>
              <td className="muted">{n.hours}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Transportation({ data, tracking, onCheck, checking }: { data: LogisticsWeek; tracking: LogisticsConfig | null; onCheck: () => void; checking: boolean }) {
  const rows = data.transportation;
  const answered = rows.filter((r) => r.plan?.submittedAt).length;
  const anyFlights = rows.some((r) => r.flights.length > 0);
  return (
    <div className="card logi">
      <h2>
        Transportation <span className="muted small">{answered} of {rows.length} answered</span>
        {tracking?.flights.enabled && anyFlights && <button type="button" className="small noprint" disabled={checking} onClick={onCheck}>{checking ? 'Checking…' : 'Check flights now'}</button>}
      </h2>
      {tracking && !tracking.flights.enabled && anyFlights && <p className="muted small noprint">Flight tracking is not switched on for this service{tracking.flights.problem ? ` (${tracking.flights.problem})` : ''} — statuses are not live.</p>}
      {tracking?.flights.enabled && <p className="muted small noprint">Statuses refresh every ten minutes; the office is emailed{tracking.email.enabled ? ` (${tracking.email.office.join(', ')})` : ' — once email is switched on —'} for a delay of 30 minutes or more, a cancellation, a diversion, or a landing.</p>}
      {/* Checked 23 Sep 2026 (D62): on a phone the Uber link opens the app with both addresses
          filled in. On a desktop it lands on uber.com with the boxes EMPTY and the city guessed
          from the browser, and Lyft's web site now just says "download our app". So the buttons
          are worth having, but only on a phone — said plainly rather than left to be discovered. */}
      {rows.some((r) => r.rides?.arrival || r.rides?.departure) && (
        <p className="muted small noprint">
          The Uber and Lyft buttons work best on a phone — they open the app with both addresses filled in. On a computer they only open the website, without the addresses. Either way you still pick the passenger and the time in the app, so a ride booked for an arriving physician is set up there, not here.
        </p>
      )}
      {rows.length === 0 ? <p className="muted small">Nobody is at the hospital this week.</p> : (
        <table className="plain">
          <thead><tr><th>Physician</th><th>This week</th><th>Airport → here</th><th>Here → airport</th><th>Flights</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.physician.id}>
                <td><strong>{dr(r.physician)}</strong></td>
                <td className="when">
                  {r.arriving && <>arrives Tue {short(r.stintStart)}</>}
                  {r.departing && <>departs Tue {short(r.stintEnd)}</>}
                  {r.onSite && !r.departing && <>on site since {short(r.stintStart)}<br /><span className="muted">departing Tue {short(r.stintEnd)}</span></>}
                  {r.arriving && <><br /><span className="muted">departing Tue {short(r.stintEnd)}</span></>}
                </td>
                <td>{transfer(r.plan, 'arrival', r.arriving)}</td>
                <td>{transfer(r.plan, 'departure', r.departing)}</td>
                <td className="small">
                  {r.flights.length ? r.flights.map((f, i) => (
                    <div key={i}>
                      {f.leg === 'arrival' ? 'in' : 'out'}: {f.airline} {f.number} · {short(f.date)} <span className={`pill tiny fl-${f.status}`}>{f.summary}</span>
                    </div>
                  )) : <span className="muted">—</span>}
                  {/* The only place the ride links live now (D62) — the physician's trip page
                      no longer offers them, because one person books the ride. */}
                  {(r.rides?.arrival || r.rides?.departure) && (
                    <div className="rides noprint">
                      {r.rides?.arrival && (
                        <span className="ride">
                          <span className="muted small">airport → {r.rides.arrivalTo}:</span>{' '}
                          <a className="ridebtn uber" href={r.rides.arrival.uber} target="_blank" rel="noopener noreferrer">Uber</a>{' '}
                          <a className="ridebtn lyft" href={r.rides.arrival.lyft} target="_blank" rel="noopener noreferrer">Lyft</a>
                        </span>
                      )}
                      {r.rides?.departure && (
                        <span className="ride">
                          <span className="muted small">{r.rides.departureFrom} → airport:</span>{' '}
                          <a className="ridebtn uber" href={r.rides.departure.uber} target="_blank" rel="noopener noreferrer">Uber</a>{' '}
                          <a className="ridebtn lyft" href={r.rides.departure.lyft} target="_blank" rel="noopener noreferrer">Lyft</a>
                        </span>
                      )}
                    </div>
                  )}
                  {r.plan?.notes && <div className="muted">“{r.plan.notes}”</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** "Yes — GSO, Tue Sep 22 2:15p → residence", "No", or "not answered" (dimmed when the leg is not this week's). */
function transfer(plan: TripPlan | null, leg: 'arrival' | 'departure', relevant: boolean) {
  if (!plan?.submittedAt) return <span className="muted">not answered</span>;
  const needs = leg === 'arrival' ? plan.needsArrivalTransfer : plan.needsDepartureTransfer;
  if (needs === false) return <span className={relevant ? '' : 'muted'}>No</span>;
  if (needs !== true) return <span className="muted">—</span>;
  const airport = leg === 'arrival' ? plan.arrivalAirport : plan.departureAirport;
  const at = leg === 'arrival' ? plan.arrivalAt : plan.departureAt;
  const place = leg === 'arrival' ? plan.arrivalDropoff : plan.departurePickup;
  return (
    <span className={relevant ? 'needs' : 'muted'}>
      <strong>Yes</strong>{airport && ` — ${airport}`}{at && `, ${when(at)}`}{place && (leg === 'arrival' ? ` → ${place}` : `, from ${place}`)}
    </span>
  );
}

/** "2027-02-02T14:15" → "Tue Feb 2, 2:15p". */
function when(s: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}):(\d{2}))?/.exec(s);
  if (!m) return s;
  const day = dayLabel(m[1]);
  if (!m[2]) return day;
  const h = Number(m[2]);
  const hour = `${h % 12 === 0 ? 12 : h % 12}${m[3] === '00' ? '' : `:${m[3]}`}${h < 12 ? 'a' : 'p'}`;
  return `${day}, ${hour}`;
}

function Groceries({ data }: { data: LogisticsWeek }) {
  return (
    <div className="card logi">
      <h2>Groceries for arrivals</h2>
      {data.groceries.length === 0 ? <p className="muted small">No grocery requests for this Tuesday's arrivals.</p> : (
        <ul className="people">
          {data.groceries.map((g) => (
            <li key={g.physician.id}>
              <strong>{dr(g.physician)}</strong>{' '}
              {g.doorDashPass && <span className="pill">DoorDash pass</span>}
              {g.items.length > 0 && <span className="muted">{g.items.join(', ')}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Housing({ data, busy, onPropose, onConfirm, onRemove, onAssign }: { data: LogisticsWeek; busy: boolean; onPropose: () => void; onConfirm: (id: string) => void; onRemove: (id: string) => void; onAssign: (physicianId: string, arriveOn: string, departOn: string) => void }) {
  return (
    <div className="card logi">
      <h2>Housing</h2>
      {data.housing.length === 0 && (
        <p className="muted small">No residences on file for {data.site.name} yet — add them under <em>Residences &amp; site guide</em>.</p>
      )}
      {data.housing.map((res) => (
        <div key={res.id} className="residence">
          <h3>{res.name} <span className="muted small">{kindLabel(res.name, res.kind)}</span></h3>
          <ul className="rooms">
            {res.rooms.map((room) => (
              <li key={room.id}>
                <span className="roomname">{room.name}</span>
                {room.occupants.length === 0 ? <span className="muted">free</span> : (
                  <ul className="occupants">
                    {room.occupants.map((o) => (
                      <li key={o.stayId} className={o.status}>
                        <span><strong>{dr(o.physician)}</strong> <span className="muted">{short(o.arriveOn)} → {short(o.departOn)}</span>{o.status === 'proposed' && <span className="pill tiny warn">proposed</span>}</span>
                        <span className="noprint">
                          {o.status === 'proposed' && <button type="button" disabled={busy} onClick={() => onConfirm(o.stayId)}>Confirm</button>}
                          <button type="button" disabled={busy} onClick={() => onAssign(o.physician.abbreviation, o.arriveOn, o.departOn)}>Move</button>
                          <button type="button" className="danger" disabled={busy} onClick={() => onRemove(o.stayId)}>Remove</button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {res.rooms.length === 0 && <li className="muted small">No rooms yet.</li>}
          </ul>
        </div>
      ))}
      <h3>Needs a room</h3>
      {data.unhoused.length === 0 ? <p className="muted small">Everyone staying this week has a room.</p> : (
        <ul className="people">
          {data.unhoused.map((u) => (
            <li key={u.physician.id}>
              <strong>{dr(u.physician)}</strong> <span className="muted">{short(u.stintStart)} → {short(u.stintEnd)}</span>
              <button type="button" className="small noprint" disabled={busy} onClick={() => onAssign(u.physician.abbreviation, u.stintStart, u.stintEnd)}>Assign…</button>
            </li>
          ))}
        </ul>
      )}
      <div className="formactions noprint">
        {data.unhoused.length > 0 && data.housing.length > 0 && <button type="button" className="primary" disabled={busy} onClick={onPropose}>Propose rooms for {data.unhoused.length}</button>}
        <button type="button" disabled={busy} onClick={() => onAssign('', data.week, addDays(data.week, 7))}>Assign someone else…</button>
      </div>
      <p className="muted small">A proposal keeps the room a physician had last time when it is free. Nobody is put in a room that already has someone else on those nights.</p>
    </div>
  );
}

function Misc({ data }: { data: LogisticsWeek }) {
  return (
    <div className="card logi">
      <h2>Residences &amp; site info</h2>
      {data.residences.map((r) => (
        <div key={r.id} className="resinfo">
          <h3>{r.name}</h3>
          {r.address && <p>{r.address}</p>}
          {r.accessNotes && <p className="small"><span className="muted">Access:</span> {r.accessNotes}</p>}
          {r.wifi && <p className="small"><span className="muted">WiFi:</span> {r.wifi}</p>}
          {r.parking && <p className="small"><span className="muted">Parking:</span> {r.parking}</p>}
          {r.notes && <p className="small muted">{r.notes}</p>}
        </div>
      ))}
      <h3>Site guide</h3>
      {data.guide ? <pre className="guide">{data.guide.body}</pre> : <p className="muted small">No site guide yet — write one under <em>Residences &amp; site guide</em>.</p>}
    </div>
  );
}

/** Put a physician in a room for a stretch of nights. */
function AssignDialog({ initial, physicians, data, onClose, onSaved }: { initial: { physicianId: string; arriveOn: string; departOn: string }; physicians: PhysicianSummary[]; data: LogisticsWeek; onClose: () => void; onSaved: () => void }) {
  const [physicianId, setPhysicianId] = useState(initial.physicianId);
  const [roomId, setRoomId] = useState('');
  const [arriveOn, setArriveOn] = useState(initial.arriveOn);
  const [departOn, setDepartOn] = useState(initial.departOn);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const save = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await api.assignStay({ physicianId, roomId, arriveOn, departOn, status: 'confirmed' });
      onSaved();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const rooms = data.housing.flatMap((res) => res.rooms.map((room) => ({ ...room, residence: res.name })));
  const free = (r: (typeof rooms)[number]) => !r.occupants.some((o) => o.physician.abbreviation !== physicianId && o.arriveOn < departOn && o.departOn > arriveOn);
  const ok = physicianId && roomId && arriveOn && departOn && departOn > arriveOn;
  return (
    <div className="overlay" role="dialog" aria-modal="true">
      <div className="modal">
        <h2>Assign a room</h2>
        <label>
          Physician
          <select value={physicianId} onChange={(e) => setPhysicianId(e.target.value)}>
            <option value="">— choose —</option>
            {physicians.map((p) => <option key={p.abbreviation} value={p.abbreviation}>{p.lastName}, {p.firstName}</option>)}
          </select>
        </label>
        <label>
          Room
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            <option value="">— choose —</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.residence} · {r.name}{free(r) ? '' : ' (taken those nights)'}</option>)}
          </select>
        </label>
        <div className="times">
          <label>Arrives<input type="date" value={arriveOn} onChange={(e) => setArriveOn(e.target.value)} /></label>
          <label>Departs<input type="date" value={departOn} onChange={(e) => setDepartOn(e.target.value)} /></label>
        </div>
        {problem && <p className="error">{problem}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" className="primary" disabled={!ok || saving} onClick={save}>{saving ? 'Saving…' : 'Assign'}</button>
        </div>
      </div>
    </div>
  );
}
